import { useForm } from "react-hook-form";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { HistoryEntryFormPayload, HistoryEntrySchema} from "@/lib/types/forms.ts";
import {usePage} from "@inertiajs/react";
import {zodResolver} from "@hookform/resolvers/zod";

interface CreateHistoryEntryDialogProps {
    isOpen: boolean;
    onOpenChange: (open: boolean) => void;
    onCreate: (data: HistoryEntryFormPayload) => Promise<void>;
    patientCardId: number;
}

export default function CreateHistoryEntryDialog({
                                                     isOpen,
                                                     onOpenChange,
                                                     onCreate,
                                                     patientCardId,
                                                 }: CreateHistoryEntryDialogProps) {

    const {translations} = usePage().props as unknown as { translations: any };
    const t = translations.create_history_dialog;

    const form = useForm<HistoryEntryFormPayload>({
        resolver: zodResolver(HistoryEntrySchema),
        defaultValues: {
            appointment_at: "",
            bill: 0,
            paid: false,
            tooth: "",
            work_done: "",
            event_id: undefined,
            patient_card_id: patientCardId,
        },
    });

    const handleSubmit = async (data: HistoryEntryFormPayload) => {
        await onCreate(data);
    };

    return (
        <Dialog open={isOpen} onOpenChange={onOpenChange}>
            <DialogContent className="max-h-[90vh] overflow-auto">
                <DialogHeader>
                    <DialogTitle>{t.title}</DialogTitle>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                        <FormField
                            control={form.control}
                            name="appointment_at"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.appointment_at}</FormLabel>
                                    <FormControl>
                                        <Input {...field} type="datetime-local"  required />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="work_done"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.work_done}</FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder={t.placeholders.work_done} value={field.value ?? ""} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="tooth"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.tooth}</FormLabel>
                                    <FormControl>
                                        <Input {...field} placeholder={t.placeholders.tooth} value={field.value ?? ""} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="bill"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t.labels.bill}</FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            placeholder={t.placeholders.bill}
                                            type="number"
                                            min={0}
                                            value={field.value ?? 0}
                                            onChange={(e) =>
                                                field.onChange(e.target.value === "" ? undefined : Number(e.target.value))
                                            }
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="paid"
                            render={({ field }) => (
                                <FormItem className="flex items-center space-x-2">
                                    <FormLabel>{t.labels.paid}</FormLabel>
                                    <FormControl>
                                        <input type="checkbox" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
                                    </FormControl>
                                </FormItem>
                            )}
                        />

                        <DialogFooter className="flex justify-end gap-2">
                            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
                                {t.buttons.cancel}
                            </Button>
                            <Button type="submit">
                                {t.buttons.save}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}

